import type {
  AuditLegalHoldItem,
  AuditLegalHoldStatus,
  AuditLegalHoldStatusFilter,
  AuditPruneApprovalDecisionRecord,
  AuditPruneApprovalItem,
  AuditPruneApprovalStatus,
  AuditPruneApprovalStatusFilter,
  AuditPruneDecision,
  AuditRetentionClass,
  ExtendAuditLegalHoldPayload,
  PlaceAuditLegalHoldPayload,
  ReleaseAuditLegalHoldPayload,
} from '../api/adminOps';
import type { ChipTone } from '../components/ui/StatusChip';

export interface Chip {
  readonly label: string;
  readonly tone: ChipTone;
}

export interface Option<T extends string> {
  readonly value: T;
  readonly label: string;
}

export const AdminGroup = {
  ADMIN: 'admin',
  /** DM-7976: may decide, like ADMIN, where a page allows operators to. */
  OPERATOR: 'admin-operator',
  AUDITOR: 'admin-auditor',
} as const;
export type AdminGroup = (typeof AdminGroup)[keyof typeof AdminGroup];

export const AUDIT_RETENTION_READ_GROUPS: readonly AdminGroup[] = [
  AdminGroup.ADMIN,
  AdminGroup.AUDITOR,
];

export const AuditRetentionTab = {
  LEGAL_HOLDS: 'legal-holds',
  PRUNE_APPROVALS: 'prune-approvals',
} as const;
export type AuditRetentionTab = (typeof AuditRetentionTab)[keyof typeof AuditRetentionTab];

export const LegalHoldStatus = {
  ACTIVE: 'active',
  RELEASED: 'released',
} as const;

export const LegalHoldStatusFilter = {
  ALL: 'all',
  ACTIVE: 'active',
  RELEASED: 'released',
} as const;

export const PruneApprovalStatus = {
  PENDING: 'pending',
  EXPIRED: 'expired',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  CONSUMED: 'consumed',
} as const;

export const PruneApprovalStatusFilter = {
  ALL: 'all',
  PENDING: 'pending',
  DECIDED: 'decided',
} as const;

export const PruneDecisionRecord = {
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
} as const;

export const PruneDecision = {
  APPROVE: 'APPROVE',
  REJECT: 'REJECT',
} as const;

export const RetentionClass = {
  STANDARD_7Y: 'standard_7y',
  EXTENDED_10Y: 'extended_10y',
  SHORT_1Y: 'short_1y',
} as const;

export const HoldAction = {
  EXTEND: 'extend',
  RELEASE: 'release',
} as const;
export type HoldAction = (typeof HoldAction)[keyof typeof HoldAction];

export const AuditRetentionOperation = {
  PLACE_LEGAL_HOLD: 'audit_legal_hold.place',
  EXTEND_LEGAL_HOLD: 'audit_legal_hold.extend',
  RELEASE_LEGAL_HOLD: 'audit_legal_hold.release',
  APPROVE_PRUNE: 'audit_prune_approval.approve',
} as const;
export type AuditRetentionOperation =
  (typeof AuditRetentionOperation)[keyof typeof AuditRetentionOperation];

export const ReferenceLabel = {
  CASE: 'Case reference',
  APPROVAL: 'Approval reference',
  DECISION: 'Decision reference',
} as const;
export type ReferenceLabel = (typeof ReferenceLabel)[keyof typeof ReferenceLabel];

export const SeparationMessage = {
  RUN_REQUESTER:
    'You requested this retention run, so another admin must decide whether its partition is pruned.',
} as const;
export type SeparationMessage = (typeof SeparationMessage)[keyof typeof SeparationMessage];

export const ALL_PARTITIONS = '*';
export const MAX_PARTITIONS = 10;
export const PARTITION_PATTERN = /^audit_events_p20\d{2}_(0[1-9]|1[0-2])$/;

const SUPPORT_REFERENCE_SOURCE =
  'DM-20\\d{2}-(?:0[1-9]|1[0-2])(?:0[1-9]|[12]\\d|3[01])-[A-HJ-NP-Z2-9]{4}';
const DM_JIRA_KEY_SOURCE = 'DM-[1-9]\\d{0,5}';
const UUID_SOURCE = '[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

export const REFERENCE_PATTERN = new RegExp(
  `^(?:${SUPPORT_REFERENCE_SOURCE}|${DM_JIRA_KEY_SOURCE}|${UUID_SOURCE})$`
);

const PARTITION_SEPARATOR = /[\s,]+/;
const SUMMARY_PARTITION_COUNT = 2;

const ADMIN_ONLY_OPERATIONS: readonly string[] = Object.values(AuditRetentionOperation);

const LEGAL_HOLD_CHIPS: Record<AuditLegalHoldStatus, Chip> = {
  [LegalHoldStatus.ACTIVE]: { label: 'Active', tone: 'warn' },
  [LegalHoldStatus.RELEASED]: { label: 'Released', tone: 'neutral' },
};

const PRUNE_APPROVAL_CHIPS: Record<AuditPruneApprovalStatus, Chip> = {
  [PruneApprovalStatus.PENDING]: { label: 'Awaiting decision', tone: 'warn' },
  [PruneApprovalStatus.EXPIRED]: { label: 'Expired', tone: 'neutral' },
  [PruneApprovalStatus.APPROVED]: { label: 'Approved', tone: 'ok' },
  [PruneApprovalStatus.REJECTED]: { label: 'Rejected', tone: 'crit' },
  [PruneApprovalStatus.CONSUMED]: { label: 'Pruned', tone: 'info' },
};

const DECISION_LABELS: Record<AuditPruneApprovalDecisionRecord, string> = {
  [PruneDecisionRecord.APPROVED]: 'Approved',
  [PruneDecisionRecord.REJECTED]: 'Rejected',
};

export const LEGAL_HOLD_FILTERS: readonly Option<AuditLegalHoldStatusFilter>[] = [
  { value: LegalHoldStatusFilter.ALL, label: 'All' },
  { value: LegalHoldStatusFilter.ACTIVE, label: 'Active' },
  { value: LegalHoldStatusFilter.RELEASED, label: 'Released' },
];

export const PRUNE_APPROVAL_FILTERS: readonly Option<AuditPruneApprovalStatusFilter>[] = [
  { value: PruneApprovalStatusFilter.ALL, label: 'All' },
  { value: PruneApprovalStatusFilter.PENDING, label: 'Pending' },
  { value: PruneApprovalStatusFilter.DECIDED, label: 'Decided or expired' },
];

export const RETENTION_CLASS_OPTIONS: readonly Option<AuditRetentionClass>[] = [
  { value: RetentionClass.STANDARD_7Y, label: 'Standard: 7 years' },
  { value: RetentionClass.EXTENDED_10Y, label: 'Extended: 10 years' },
  { value: RetentionClass.SHORT_1Y, label: 'Short: 1 year' },
];

export function requiresAdminDecision(operation: string): boolean {
  return ADMIN_ONLY_OPERATIONS.includes(operation);
}

export function parsePartitions(text: string): string[] {
  const entries = text.split(PARTITION_SEPARATOR).filter(entry => entry.length > 0);
  return [...new Set(entries)];
}

export function isPartitionName(value: string): boolean {
  return value === ALL_PARTITIONS || PARTITION_PATTERN.test(value);
}

export function partitionsBlocker(partitions: readonly string[]): string | null {
  if (partitions.length === 0) return 'List at least one partition';
  if (partitions.length > MAX_PARTITIONS) {
    return `List at most ${MAX_PARTITIONS} partitions per request`;
  }
  const invalid = partitions.find(partition => !isPartitionName(partition));
  if (invalid !== undefined) {
    return `${invalid} is not an audit partition (audit_events_pYYYY_MM or ${ALL_PARTITIONS})`;
  }
  return null;
}

export function referenceBlocker(label: ReferenceLabel, value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return `${label} is required`;
  if (!REFERENCE_PATTERN.test(trimmed)) {
    return `${label} must be a support reference (DM-YYYY-MMDD-XXXX), a Jira key (DM-1234) or a lowercase UUID`;
  }
  return null;
}

export function heldPartitions(hold: AuditLegalHoldItem): string[] {
  return [
    ...new Set([
      ...hold.partitions,
      ...hold.extensions.flatMap(extension => extension.added_partitions),
    ]),
  ];
}

export function partitionsNotYetHeld(
  hold: AuditLegalHoldItem,
  partitions: readonly string[]
): string[] {
  const held = heldPartitions(hold);
  if (held.includes(ALL_PARTITIONS)) return [];
  return partitions.filter(partition => !held.includes(partition));
}

export interface PlaceHoldDraft {
  readonly caseId: string;
  readonly approvalRef: string;
  readonly partitionsText: string;
}

export interface ExtendHoldDraft {
  readonly approvalRef: string;
  readonly partitionsText: string;
}

export interface ReleaseHoldDraft {
  readonly approvalRef: string;
  readonly retentionClass: AuditRetentionClass | null;
}

export type CheckedRequest<T> =
  | { readonly blocker: string; readonly payload: null }
  | { readonly blocker: null; readonly payload: T };

function blocked<T>(blocker: string): CheckedRequest<T> {
  return { blocker, payload: null };
}

function ready<T>(payload: T): CheckedRequest<T> {
  return { blocker: null, payload };
}

export function placeHoldRequest(
  draft: PlaceHoldDraft
): CheckedRequest<PlaceAuditLegalHoldPayload> {
  const partitions = parsePartitions(draft.partitionsText);
  const problem =
    referenceBlocker(ReferenceLabel.CASE, draft.caseId) ??
    referenceBlocker(ReferenceLabel.APPROVAL, draft.approvalRef) ??
    partitionsBlocker(partitions);
  if (problem !== null) return blocked(problem);
  return ready({
    case_id: draft.caseId.trim(),
    approval_ref: draft.approvalRef.trim(),
    partitions,
  });
}

export function extendHoldRequest(
  hold: AuditLegalHoldItem,
  draft: ExtendHoldDraft
): CheckedRequest<ExtendAuditLegalHoldPayload> {
  if (hold.status !== LegalHoldStatus.ACTIVE) return blocked('Only an active hold can be extended');
  const partitions = parsePartitions(draft.partitionsText);
  const problem =
    referenceBlocker(ReferenceLabel.APPROVAL, draft.approvalRef) ?? partitionsBlocker(partitions);
  if (problem !== null) return blocked(problem);
  const added = partitionsNotYetHeld(hold, partitions);
  if (added.length === 0) return blocked('The hold already covers every partition listed');
  return ready({ approval_ref: draft.approvalRef.trim(), partitions: added });
}

export function releaseHoldRequest(
  hold: AuditLegalHoldItem,
  draft: ReleaseHoldDraft
): CheckedRequest<ReleaseAuditLegalHoldPayload> {
  if (hold.status !== LegalHoldStatus.ACTIVE) return blocked('This hold is already released');
  const problem = referenceBlocker(ReferenceLabel.APPROVAL, draft.approvalRef);
  if (problem !== null) return blocked(problem);
  if (draft.retentionClass === null) {
    return blocked('Choose the retention class that applies after release');
  }
  return ready({
    approval_ref: draft.approvalRef.trim(),
    post_release_retention_class: draft.retentionClass,
  });
}

export function isPrunePending(approval: AuditPruneApprovalItem): boolean {
  return approval.status === PruneApprovalStatus.PENDING;
}

export function pruneDecisionReference(
  approval: AuditPruneApprovalItem,
  decisionRef: string
): CheckedRequest<string> {
  if (!isPrunePending(approval)) {
    return blocked('Only a request awaiting a decision can be decided');
  }
  const problem = referenceBlocker(ReferenceLabel.DECISION, decisionRef);
  return problem === null ? ready(decisionRef.trim()) : blocked(problem);
}

export interface PruneResend {
  readonly decision: AuditPruneDecision;
  readonly decisionRef: string;
}

const RESEND_DECISION: Record<AuditPruneApprovalDecisionRecord, AuditPruneDecision> = {
  [PruneDecisionRecord.APPROVED]: PruneDecision.APPROVE,
  [PruneDecisionRecord.REJECTED]: PruneDecision.REJECT,
};

const RESENDABLE_STATUSES: readonly AuditPruneApprovalStatus[] = [
  PruneApprovalStatus.APPROVED,
  PruneApprovalStatus.REJECTED,
];

export function pruneResendRequest(
  approval: AuditPruneApprovalItem,
  now: number
): PruneResend | null {
  if (!RESENDABLE_STATUSES.includes(approval.status)) return null;
  if (approval.decision === null || approval.decision_ref === null) return null;
  if (!(Date.parse(approval.expires_at) > now)) return null;
  return { decision: RESEND_DECISION[approval.decision], decisionRef: approval.decision_ref };
}

export function isRunRequester(
  approval: Pick<AuditPruneApprovalItem, 'requested_by'>,
  userId: string | null | undefined
): boolean {
  return typeof userId === 'string' && userId.length > 0 && approval.requested_by === userId;
}

export function pruneRequesterBlocker(
  approval: AuditPruneApprovalItem,
  userId: string | null | undefined
): SeparationMessage | null {
  return isRunRequester(approval, userId) ? SeparationMessage.RUN_REQUESTER : null;
}

export function approverSeparationBlocker(
  operation: string,
  payload: unknown,
  userId: string | null | undefined
): SeparationMessage | null {
  if (operation !== AuditRetentionOperation.APPROVE_PRUNE) return null;
  if (typeof payload !== 'object' || payload === null || !('requested_by' in payload)) return null;
  const requestedBy = payload.requested_by;
  if (typeof requestedBy !== 'string') return null;
  return isRunRequester({ requested_by: requestedBy }, userId)
    ? SeparationMessage.RUN_REQUESTER
    : null;
}

export function retentionClassFrom(value: string): AuditRetentionClass | null {
  return RETENTION_CLASS_OPTIONS.find(option => option.value === value)?.value ?? null;
}

export function legalHoldStatusChip(status: AuditLegalHoldStatus): Chip {
  return LEGAL_HOLD_CHIPS[status] ?? { label: status, tone: 'neutral' };
}

export function pruneApprovalStatusChip(status: AuditPruneApprovalStatus): Chip {
  return PRUNE_APPROVAL_CHIPS[status] ?? { label: status, tone: 'neutral' };
}

export function decisionLabel(decision: AuditPruneApprovalDecisionRecord | null): string | null {
  if (decision === null) return null;
  return DECISION_LABELS[decision] ?? decision;
}

export function partitionLabel(partition: string): string {
  return partition === ALL_PARTITIONS ? `All partitions (${ALL_PARTITIONS})` : partition;
}

export function partitionsSummary(partitions: readonly string[]): string {
  if (partitions.includes(ALL_PARTITIONS)) return partitionLabel(ALL_PARTITIONS);
  if (partitions.length === 0) return '—';
  const shown = partitions.slice(0, SUMMARY_PARTITION_COUNT).join(', ');
  const hidden = partitions.length - SUMMARY_PARTITION_COUNT;
  return hidden > 0 ? `${shown} +${hidden} more` : shown;
}
